// frontend/src/contexts/AppContext.tsx
/**
 * App Context - Provides shared application state
 * This context shares the database user (dbUser) state across all components
 * including AdminRoute, which needs to check admin permissions.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode
} from 'react';
import { useAuth } from './AuthContext';
import { useActiveInstitution } from './ActiveInstitutionContext';
import {
  userService,
  NeedsProvisioningError,
  type SyncUserResponse,
  type TermsState,
} from '../services/userService';
import { typedApi } from '../services/typedApiClient';
import { subscribeToTermsRequired } from '../utils/appEvents';

// =============================================================================
// CONSTANTS
// =============================================================================

// Phase F: cache key suffixed with the institution slug so two
// institutions don't share a cached dbUser when a user crosses subdomains.
// The pre-Phase-F unscoped key `academic_planner_db_user` is removed on
// first load by `migrateLegacyCacheKeys()` below.
// Phase E3 (2026-05-16): slug derivation shifted from host-only to
// active-membership-first (host fallback for pre-auth). Single-membership
// users see identical key values, so no prefix bump needed.
const CACHE_KEY_PREFIX = 'academic_planner_db_user_v2';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_SLUG = 'umd';

/** Resolve the cache key for the given institution slug. Matches the
 *  apiClient's `X-Institution-Slug` value so cache and request agree
 *  on identity. */
const cacheKey = (slug: string): string => `${CACHE_KEY_PREFIX}:${slug}`;

/** One-time cleanup of the pre-Phase-F unscoped key. Idempotent. */
const migrateLegacyCacheKeys = (): void => {
  try {
    localStorage.removeItem('academic_planner_db_user');
  } catch {
    // ignored
  }
};
// Fire once on module load — first import wins, subsequent imports are no-ops.
migrateLegacyCacheKeys();

// =============================================================================
// TYPES
// =============================================================================

export interface DbUser extends SyncUserResponse {
  profile_data?: {
    password_set?: boolean;
    [key: string]: unknown;
  };
  preferences?: {
    has_majors_in_plan?: boolean;
    has_semesters_in_plan?: boolean;
    [key: string]: unknown;
  };
}

// Why sync refused an account (NeedsProvisioningError, open-access plan §3.9).
export interface ProvisioningRefusal {
  reason: string;
  eligibleDomains: string[];
}

// Internal type for cached data with timestamp
interface CachedDbUser extends DbUser {
  _cacheTimestamp?: number;
}

export interface AppContextValue {
  // Database user state (shared across all components)
  dbUser: DbUser | null;
  setDbUser: React.Dispatch<React.SetStateAction<DbUser | null>>;
  syncingUser: boolean;
  setSyncingUser: React.Dispatch<React.SetStateAction<boolean>>;

  // Refresh user data from database
  refreshUser: () => Promise<DbUser | null>;

  // Re-run sync for the provisioning screen's "Try again": throws on a
  // failure that is not a refusal, where refreshUser logs and returns null.
  retrySync: () => Promise<void>;

  // True when the session is authenticated but the backend has no account for
  // it yet (sync returned needs_provisioning). The app shows a "finish
  // joining" interstitial instead of the main UI. See docs/plans/auth-google-sso/.
  needsProvisioning: boolean;

  // Why sync refused an account, and which email domains currently have open
  // access, for the NeedsProvisioning copy (open-access plan §3.9). Null unless
  // needsProvisioning.
  provisioningRefusal: ProvisioningRefusal | null;

  // True when the user must accept the current Terms of Service and Privacy
  // Policy before using the app (open-access plan §3.3). The app shows the
  // TermsGate instead of the main UI.
  termsRequired: boolean;

  // Record acceptance of the current terms (POST /api/users/me/accept-terms).
  // On a stale version (409) it re-syncs to pick up the current one, then
  // throws so the gate can ask again. Throws on failure.
  acceptTerms: () => Promise<void>;

  // Derived admin status
  isAdmin: boolean;
}

// =============================================================================
// CONTEXT
// =============================================================================

export const AppContext = createContext<AppContextValue | null>(null);

export const useApp = (): AppContextValue => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const { user } = useAuth();
  // Phase E3: derive the cache lookup slug from the active membership so a
  // multi-membership user's dbUser cache is keyed by their *selected*
  // school. Pre-auth: DEFAULT (subdomain host fallback removed 2026-07-17).
  const {
    activeSlug,
    active: activeMembership,
    loading: membershipsLoading,
    refresh: refreshMemberships,
  } = useActiveInstitution();
  const resolvedSlug = activeSlug ?? DEFAULT_SLUG;

  const [dbUser, setDbUser] = useState<DbUser | null>(null);
  const [syncingUser, setSyncingUser] = useState(true); // Start as true to prevent flash
  const [needsProvisioning, setNeedsProvisioning] = useState(false);
  const [provisioningRefusal, setProvisioningRefusal] = useState<ProvisioningRefusal | null>(null);

  // Handle the needs_provisioning control signal (authenticated, no account
  // yet): surface it as a flag, clear any cached user, and never fabricate a
  // broken dbUser. Returns true if handled; callers log their own message for
  // any other (genuine) error.
  const handleSyncError = useCallback((error: unknown): boolean => {
    if (error instanceof NeedsProvisioningError) {
      setNeedsProvisioning(true);
      setProvisioningRefusal({ reason: error.reason, eligibleDomains: error.eligibleDomains });
      setDbUser(null);
      try {
        localStorage.removeItem(cacheKey(resolvedSlug));
      } catch {
        // ignored
      }
      return true;
    }
    return false;
  }, [resolvedSlug]);

  // Core sync logic: call API, update state, and persist to localStorage
  const applySyncResult = useCallback((syncedUser: SyncUserResponse | null): DbUser | null => {
    if (!syncedUser) return null;
    // A successful sync clears any prior needs_provisioning state.
    setNeedsProvisioning(false);
    setProvisioningRefusal(null);
    const dbUserResult = syncedUser as DbUser;
    setDbUser(dbUserResult);

    // Add cache timestamp when saving
    const cachedUser: CachedDbUser = {
      ...syncedUser,
      _cacheTimestamp: Date.now()
    };
    localStorage.setItem(cacheKey(resolvedSlug), JSON.stringify(cachedUser));

    return dbUserResult;
  }, [resolvedSlug]);

  // Refresh user data from database - called after profile updates
  const refreshUser = useCallback(async (): Promise<DbUser | null> => {
    if (!user) return null;

    try {
      return applySyncResult(await userService.syncUser(user));
    } catch (error) {
      if (!handleSyncError(error)) {
        console.error('Failed to refresh user:', error);
      }
      return null;
    }
  }, [user, applySyncResult, handleSyncError]);

  // Re-run sync from the provisioning screen's "Try again". Unlike
  // refreshUser, a failure that is not a refusal (network, 5xx) is thrown so
  // the screen can say so; a refusal updates the screen's reason as usual.
  const retrySync = useCallback(async (): Promise<void> => {
    if (!user) return;
    try {
      applySyncResult(await userService.syncUser(user));
    } catch (error) {
      if (!handleSyncError(error)) throw error;
    }
  }, [user, applySyncResult, handleSyncError]);

  const acceptTerms = useCallback(async (): Promise<void> => {
    const version = dbUser?.terms?.current_version;
    if (!version) {
      await refreshUser();
      throw new Error('Please try again.');
    }
    try {
      const result = await typedApi.users.acceptTerms({ version });
      // The generated type marks every field optional (dump_only schema, the
      // same F11 gap userService.syncUser documents); the backend always sends
      // all four, so cast once current_version is present.
      if (dbUser && result?.terms?.current_version) {
        applySyncResult({ ...dbUser, terms: result.terms as TermsState });
      } else {
        await refreshUser();
      }
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      if (status === 409) {
        // The terms changed while the gate was open: pick up the new version
        // and ask again rather than recording an acceptance of old text.
        await refreshUser();
        throw new Error('The terms were just updated. Please review them and agree again.');
      }
      throw error;
    }
  }, [dbUser, applySyncResult, refreshUser]);

  // ActiveInstitutionProvider fetches the memberships once per identity. For a
  // brand-new account that fetch can run before sync creates the membership:
  // on first sign-in the two requests race, and "Try again" on the
  // NeedsProvisioning screen provisions long after it. The active school would
  // then stay unknown, and an SJSU student would see UMD (the default) until a
  // reload. So when sync has a user but no active membership is known, fetch
  // the memberships again, once per user.
  const membershipsRefetchedFor = useRef<number | null>(null);
  useEffect(() => {
    const dbUserId = dbUser?.id ?? null;
    if (dbUserId === null || activeMembership || membershipsLoading) return;
    if (membershipsRefetchedFor.current === dbUserId) return;
    membershipsRefetchedFor.current = dbUserId;
    void refreshMemberships();
  }, [dbUser?.id, activeMembership, membershipsLoading, refreshMemberships]);

  // A Claude surface refused with `terms_required`: re-sync, which shows the
  // TermsGate only if acceptance really is missing (the backend fails closed
  // on a lookup error, so a lone refusal is not proof).
  useEffect(() => subscribeToTermsRequired(() => { void refreshUser(); }), [refreshUser]);

  // Sync database user when Supabase user changes
  useEffect(() => {
    // Clear dbUser immediately when user changes to prevent stale data
    setDbUser(null);
    setNeedsProvisioning(false);
    setProvisioningRefusal(null);

    if (!user) {
      // NOTE: We intentionally do NOT set setSyncingUser(false) here.
      // When user transitions from null → non-null, React runs child effects
      // before parent effects. If we set syncingUser=false when user is null,
      // there's a render where syncingUser=false AND user is non-null (before
      // AppContext's effect runs to start sync), causing child hooks like
      // usePlanCore to fire API calls before sync creates the DB record → 401.
      // Keeping syncingUser=true when user is null is harmless because App.js
      // renders <SupabaseAuth /> (not the main app) when user is null.
      return;
    }

    const syncDatabaseUser = async () => {
      try {
        setSyncingUser(true);
        applySyncResult(await userService.syncUser(user));
      } catch (error) {
        if (!handleSyncError(error)) {
          console.error('Failed to sync user:', error);
        }
      } finally {
        setSyncingUser(false);
      }
    };

    // Check localStorage first for faster initial load
    const cachedUser = localStorage.getItem(cacheKey(resolvedSlug));
    if (cachedUser) {
      try {
        const parsed = JSON.parse(cachedUser) as CachedDbUser;

        // Check cache validity
        const cacheAge = Date.now() - (parsed._cacheTimestamp || 0);
        const isCacheStale = cacheAge > CACHE_TTL_MS;

        // Verify the cached user matches current auth user AND has required fields
        // Note: role field was added in v5.x, so older cached users may not have it
        // If role is missing, skip cache and force a fresh sync. Same for terms
        // (8.66.0): a cache written before the field existed must not let a user
        // past the TermsGate for up to the cache TTL.
        if (parsed.email === user.email && parsed.role !== undefined && parsed.terms !== undefined) {
          // Immediately show cached data (remove timestamp from display)
          const { _cacheTimestamp, ...dbUserData } = parsed;
          setDbUser(dbUserData as DbUser);

          // Only sync if cache is stale
          if (isCacheStale) {
            console.log(`[AppContext] Cache stale (${Math.round(cacheAge / 1000)}s old), syncing`);
            (async () => {
              try {
                applySyncResult(await userService.syncUser(user));
              } catch (error) {
                if (!handleSyncError(error)) {
                  console.error('Background sync failed:', error);
                }
              } finally {
                setSyncingUser(false);
              }
            })();
          } else {
            console.log(`[AppContext] Using fresh cache (${Math.round(cacheAge / 1000)}s old)`);
            setSyncingUser(false);
          }
          return;
        } else {
          // Cache is for a different user, clear it
          localStorage.removeItem(cacheKey(resolvedSlug));
        }
      } catch {
        localStorage.removeItem(cacheKey(resolvedSlug));
      }
    }

    syncDatabaseUser();
  // Phase F3 (2026-05-16): also depend on activeSlug so a school switch
  // (via SchoolSwitcher header or sidebar dropdown) triggers a fresh
  // sync. Without this, dbUser holds the previous school's per-school
  // fields (major, enrollment dates) until the user logs out and back in.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeSlug]);

  // Memoize isAdmin to avoid recomputation on every render
  const isAdmin = useMemo(() => dbUser?.role === 'admin', [dbUser?.role]);
  const termsRequired = dbUser?.terms?.required === true;

  // Memoize context value to prevent unnecessary re-renders of consuming components
  // Only recreate when dependencies actually change
  const value: AppContextValue = useMemo(
    () => ({
      // Database user state (shared across all components)
      dbUser,
      setDbUser,
      syncingUser,
      setSyncingUser,

      // Refresh user data from database
      refreshUser,
      retrySync,

      // Authenticated-but-unprovisioned signal and why
      needsProvisioning,
      provisioningRefusal,

      // Terms consent gate
      termsRequired,
      acceptTerms,

      // Derived admin status
      isAdmin,
    }),
    [dbUser, syncingUser, refreshUser, retrySync, needsProvisioning, provisioningRefusal,
     termsRequired, acceptTerms, isAdmin]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
